import { bangkokDayKey } from './history-search';

// Pure aggregates for the charging-cost dashboard. Rows must already be the
// latest revision per event (the get_history RPC guarantees that). Blank cost
// or kWh counts as 0 in sums — never invented — and ratios stay null whenever
// a denominator is zero instead of reporting a misleading rate.
export interface ChargeDashboardEvent { eventId: string; occurredAt: string; type: string; locationName?: string | null; odometer?: number | null; chargeKwh?: number | null; cost?: number | null; }
export interface ChargeMonthStats { month: string | null; cost: number; kwh: number; charges: number; distanceKm: number; bahtPerKwh: number | null; kmPerKwh: number | null; bahtPerKm: number | null; }
export interface ChargeLocationShare { name: string; charges: number; cost: number; kwh: number; share: number; }

// Bangkok calendar month (YYYY-MM) — the same boundary the day keys use.
export const bangkokMonthKey = (iso: string) => bangkokDayKey(iso).slice(0, 7);

export const previousMonthKey = (month: string) => {
  const [year, monthNumber] = month.split('-').map(Number);
  return monthNumber === 1 ? `${year - 1}-12` : `${year}-${String(monthNumber - 1).padStart(2, '0')}`;
};

const round = (value: number, digits = 2) => Math.round((value + Number.EPSILON) * 10 ** digits) / 10 ** digits;
const num = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

// Distance for the period is the odometer span (max − min) across every
// reading in it — the same formula the SQL/LINE summaries use. Summing per-day
// spans would drop the leg driven between the last reading of one day and the
// first of the next (e.g. a return home recorded after midnight).
const odometerSpan = (rows: readonly ChargeDashboardEvent[], month: string | null) => {
  const readings = rows
    .filter(row => row.odometer != null && (!month || bangkokDayKey(row.occurredAt).startsWith(month)))
    .map(row => num(row.odometer));
  return readings.length ? Math.max(...readings) - Math.min(...readings) : 0;
};

const ratios = (cost: number, kwh: number, distanceKm: number) => ({
  bahtPerKwh: kwh > 0 && cost > 0 ? round(cost / kwh) : null,
  kmPerKwh: kwh > 0 && distanceKm > 0 ? round(distanceKm / kwh) : null,
  bahtPerKm: distanceKm > 0 && cost > 0 ? round(cost / distanceKm) : null,
});

// month === null aggregates the whole history instead of one Bangkok month.
export function chargeMonthStats(rows: readonly ChargeDashboardEvent[], month: string | null): ChargeMonthStats {
  const charges = rows.filter(row => row.type === 'charge' && (!month || bangkokMonthKey(row.occurredAt) === month));
  const cost = round(charges.reduce((sum, row) => sum + num(row.cost), 0));
  const kwh = round(charges.reduce((sum, row) => sum + num(row.chargeKwh), 0), 3);
  const distanceKm = round(odometerSpan(rows, month));
  return { month, cost, kwh, charges: charges.length, distanceKm, ...ratios(cost, kwh, distanceKm) };
}

// Bangkok months that contain at least one charge event, newest first.
export function chargeMonthKeys(rows: readonly ChargeDashboardEvent[], limit?: number): string[] {
  const months = new Set<string>();
  for (const row of rows) if (row.type === 'charge') months.add(bangkokMonthKey(row.occurredAt));
  return [...months].sort((a, b) => b.localeCompare(a)).slice(0, limit);
}

export function chargeMonthSeries(rows: readonly ChargeDashboardEvent[], months: readonly string[]): ChargeMonthStats[] {
  return months.map(month => chargeMonthStats(rows, month));
}

export function topChargeLocations(rows: readonly ChargeDashboardEvent[], month: string | null, limit = 3): ChargeLocationShare[] {
  const charges = rows.filter(row => row.type === 'charge' && (!month || bangkokMonthKey(row.occurredAt) === month));
  const total = charges.reduce((sum, row) => sum + num(row.cost), 0);
  const byName = new Map<string, { charges: number; cost: number; kwh: number }>();
  for (const row of charges) {
    const name = (row.locationName ?? '').trim() || 'ไม่ระบุสถานที่';
    const acc = byName.get(name) ?? { charges: 0, cost: 0, kwh: 0 };
    acc.charges++; acc.cost += num(row.cost); acc.kwh += num(row.chargeKwh);
    byName.set(name, acc);
  }
  return [...byName].map(([name, acc]) => ({ name, charges: acc.charges, cost: round(acc.cost), kwh: round(acc.kwh, 3), share: total > 0 ? round(acc.cost / total * 100, 1) : 0 }))
    .sort((a, b) => b.cost - a.cost || a.name.localeCompare(b.name, 'th')).slice(0, Math.max(0, limit));
}
